import { HttpClient } from '@/shared';
import { getEnvironmentConfig } from '@/platform/application/config/environment';
import { I9Record } from '../../application/domain/entities/EmployeeRecord';
import { DocumentSection } from '../../application/domain/entities/Document';
import {
  EmployerLocationOption,
  II9FormRecordRepository,
  ImportedDocuments,
  ImportedDocumentTarget,
  ReplacedCase,
} from '../../application/ports/outbound/IEmployeeRecordRepository';
import { X_API_KEY } from '@/shared/infra/http/runtimeEnv';
import { InvitationLocationItemDto } from '@/features/invitation/infra/repositories/dto/res/InvitationLocationItemDto';
import { EmployeeRecordResponseDto } from './dto/res/EmployeeRecordResponseDto';
import { DocumentsResponseDto } from './dto/res/DocumentResponseDto';
import { EmployeeRecordMapper } from './mappers/EmployeeRecordMapper';
import { DocumentMapper } from './mappers/DocumentMapper';
import CustomDocumentType from '@/features/section-2/application/domain/entities/CustomDocumentType';
import { AmendmentOptions } from '../../application/domain/entities/enums/i9Form-amendment';
import { EmployeeProvidedDocsGetResponseDto } from '@/features/section-1/infra/repositories/dto/res/EmployeeProvidedDocsResponseDto';

export class ApiI9FormRecordRepository implements II9FormRecordRepository {
  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Fetch employee record details by I9 Form ID
   */
  async fetchI9FormRecord(i9FormId: string): Promise<I9Record> {
    // TODO: Uncomment when API is ready
    const response = await this.httpClient.get<EmployeeRecordResponseDto>(
      `/i9Form/${i9FormId}/details`
    );
    return EmployeeRecordMapper.toDomain(response.data);
  }

  /**
   * VTK-1991 — the files a vendor delivery carried for this record.
   *
   * Separate from `fetchDocuments`, which returns the record's Section 2 documents. These are the
   * raw delivered files, most of which nothing has identified yet.
   */
  async fetchImportedDocuments(i9FormId: string): Promise<ImportedDocuments> {
    const response = await this.httpClient.get<ImportedDocuments>(
      `/digitization/records/${i9FormId}/imported-documents`
    );
    return response.data ?? { files: [], slots: [] };
  }

  async classifyImportedDocument(
    i9FormId: string,
    importedDocumentId: string,
    target: ImportedDocumentTarget | null
  ): Promise<void> {
    await this.httpClient.patch(
      `/digitization/records/${i9FormId}/imported-documents/${importedDocumentId}`,
      target ? { list: target.list, side: target.side } : {}
    );
  }

  importedDocumentUrl(i9FormId: string, importedDocumentId: string): string {
    const base = getEnvironmentConfig().apiBaseUrlCore.replace(/\/$/, '');
    return (
      `${base}/digitization/records/${encodeURIComponent(i9FormId)}` +
      `/imported-documents/${encodeURIComponent(importedDocumentId)}/file`
    );
  }

  /**
   * The file's bytes. Raw `fetch` rather than the shared client: this returns a stream, not the
   * envelope the client unwraps, and the same cookie + key scheme the other document endpoints use
   * (`fetchRecordPdf`) is what an `<img src>` or `<iframe src>` cannot carry on its own.
   */
  async fetchImportedDocumentFile(i9FormId: string, importedDocumentId: string): Promise<Blob> {
    const response = await fetch(this.importedDocumentUrl(i9FormId, importedDocumentId), {
      credentials: 'include',
      headers: { 'X-Api-Key': X_API_KEY },
    });
    if (!response.ok) throw new Error(`Could not load the file (${response.status})`);
    return response.blob();
  }

  async fetchReplacedCases(i9FormId: string): Promise<ReplacedCase[]> {
    const response = await this.httpClient.get<ReplacedCase[]>(
      `/digitization/records/${i9FormId}/replaced-cases`
    );
    return response.data ?? [];
  }

  /** Raw `fetch` for the same reason as {@link fetchImportedDocumentFile}: it is a stream. */
  async fetchReplacedCaseFile(
    i9FormId: string,
    replacedI9FormId: string,
    importedDocumentId: string
  ): Promise<Blob> {
    const base = getEnvironmentConfig().apiBaseUrlCore.replace(/\/$/, '');
    const url =
      `${base}/digitization/records/${encodeURIComponent(i9FormId)}/replaced-cases/` +
      `${encodeURIComponent(replacedI9FormId)}/files/${encodeURIComponent(importedDocumentId)}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'X-Api-Key': X_API_KEY },
    });
    if (!response.ok) throw new Error(`Could not load the file (${response.status})`);
    return response.blob();
  }

  /**
   * Fetch document URLs for an I9 form
   * @param i9FormId - The ID of the I9 form
   * @param customDocumentTypes - Optional array of CustomDocumentType from Redux for display names
   */
  async fetchDocuments(
    i9FormId: string,
    customDocumentTypes?: CustomDocumentType[]
  ): Promise<DocumentSection[]> {
    const response = await this.httpClient.get<DocumentsResponseDto>(
      `/i9Form/${i9FormId}/document-urls`
    );
    return DocumentMapper.toDomain(response.data, customDocumentTypes);
  }

  async fetchEmployeeProvidedDocs(
    i9FormId: string
  ): Promise<EmployeeProvidedDocsGetResponseDto | null> {
    const response = await this.httpClient.get<EmployeeProvidedDocsGetResponseDto>(
      `/employee-provided-docs/${i9FormId}`
    );
    return response.data;
  }

  /**
   * Amend section 1 of the I9 form
   * @param i9FormId - The ID of the I9 form
   * @param amendmentOption - The section to amend
   * @param email - The email of the employee (only required for section 1 amendments)
   * @param amendmentReason - The reason for the amendment
   */
  async amendI9Form(
    i9FormId: string,
    amendmentOption: AmendmentOptions,
    amendmentReason: string,
    email: string | undefined
  ): Promise<void> {
    await this.httpClient.post(`amendment/trigger/${i9FormId}`, {
      i9FormId,
      amendmentOption,
      amendmentReason,
      ...(amendmentOption === AmendmentOptions.SECTION1 && { email }),
    });
  }

  /**
   * VTK-1954 — hire date and/or location on a pending I-9.
   *
   * Only the fields actually being changed are sent. The API leaves an omitted field alone, and it
   * compares before writing, so sending both every time would be harmless — but sending only what
   * changed keeps the intent of the request obvious in a log or a network tab.
   */
  async updateEmploymentDetails(
    i9FormId: string,
    details: { hireDate?: string; locationId?: string }
  ): Promise<void> {
    await this.httpClient.patch(`/i9Form/${i9FormId}/employment-details`, {
      ...(details.hireDate !== undefined && { hireDate: details.hireDate }),
      ...(details.locationId !== undefined && { locationId: details.locationId }),
    });
  }

  async fetchEmployerLocations(keepLocationId?: string): Promise<EmployerLocationOption[]> {
    // The list holds active sites only, since it is also the invitation picker. A record already on
    // an inactive (historical) site passes it, so it still shows, and may keep, its own (VTK-2031).
    const response = await this.httpClient.get<InvitationLocationItemDto[]>(
      '/location/employer-locations',
      keepLocationId ? { params: { keepLocationId } } : undefined
    );
    if (!Array.isArray(response?.data)) {
      throw new Error('Invalid employer locations response');
    }
    return response.data.map((dto) => ({ locationId: dto.locationId, name: dto.locationName }));
  }
}
